var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/editor/x-editor.ts
import "@adornis/buildify/client/components/x-buildify-size-picker";
import { Contexts } from "@adornis/buildify/client/globals/enums.js";
import { RXController } from "@adornis/chemistry/controllers/RXController.js";
import { css } from "@adornis/chemistry/directives/css.js";
import "@adornis/chemistry/elements/components/x-accordeon.js";
import "@adornis/chemistry/elements/components/x-flex";
import "@adornis/chemistry/elements/components/x-icon";
import { FormField } from "@adornis/formfield/form-field.js";
import { ContextProvider, createContext } from "@lit/context";
import { html, css as litCSS, nothing, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { baseKeymap } from "prosemirror-commands";
import { keymap } from "prosemirror-keymap";
import { DOMParser, DOMSerializer, Schema } from "prosemirror-model";
import { EditorState } from "prosemirror-state";
import { EditorView } from "prosemirror-view";
import { combineLatest, debounceTime } from "rxjs";
import { EditGlobalSettingsMenuItem } from "../menu-items/EditGlobalSettings.js";
import { EditSelectedNodeMenuItem } from "../menu-items/EditSelectedNode.js";
import { EnsureDocNodeAttrs, setDocAttribute } from "../plugins/EnsureDocNodeAttrs.js";
import { handleTransactionMetaPlugin } from "../plugins/handleTransactionMetaPlugin.js";
import { SyncWrapperWithDocPlugin } from "../plugins/SyncWrapperWithDocPlugin.js";
import "../types.js";
import { plainEditorConfig } from "./presets/plain-editor.js";
import "./toolbar/x-editor-toolbar.js";
var EDITOR_ID = "editor";
function getHtmlString(doc, stylings) {
  const serializer = DOMSerializer.fromSchema(doc.type.schema);
  const docElement = serializer.serializeNode(doc);
  for (const [key, value] of Object.entries(doc.attrs)) {
    if (typeof value !== "string" || !value)
      continue;
    docElement.setAttribute(key, value);
  }
  const styleElement = document.createElement("style");
  styleElement.textContent = litCSS`
    ${unsafeCSS(stylings)}
  `.cssText;
  docElement.appendChild(styleElement);
  const wrapper = document.createElement("div");
  wrapper.appendChild(docElement);
  return wrapper.innerHTML;
}
var XEditor = class extends FormField {
  constructor() {
    super(...arguments);
    this.configs = new RXController(
      this,
      plainEditorConfig()
    );
    this.globalSettingsClassName = new RXController(this, void 0);
    this.sizeBreakpoints = new RXController(this, void 0);
    this._globalSettingsClassNameProvider = new ContextProvider(this, {
      context: createContext(Contexts.GLOBAL_SETTINGS_CLASS_NAME)
    });
    // * lifecycle hooks
    this._menuItems = [];
  }
  getPlugins(schema) {
    const plugins = [];
    for (const config of this.configs.value) {
      if (!config.plugins)
        continue;
      plugins.push(...config.plugins(schema));
    }
    plugins.push(
      handleTransactionMetaPlugin("onNodeChange", () => {
        setTimeout(() => this.commitValue("changed"));
      })
    );
    plugins.push(
      EnsureDocNodeAttrs({
        lang: "de",
        style: "hyphens: auto;"
      })
    );
    const docElement = this.renderRoot.querySelector(`#${EDITOR_ID}`);
    if (docElement)
      plugins.push(SyncWrapperWithDocPlugin(docElement));
    plugins.push(keymap(baseKeymap));
    return plugins;
  }
  getStylings() {
    const stylings = [];
    for (const config of this.configs.value) {
      if (!config.stylings)
        continue;
      stylings.push(config.stylings);
    }
    return stylings;
  }
  generateSchema() {
    const nodes = {};
    const marks = {};
    for (const config of this.configs.value) {
      if ("mark" in config) {
        marks[config.mark.name] = config.mark.schema;
      }
      if ("node" in config) {
        nodes[config.node.name] = config.node.schema;
      }
    }
    return new Schema({
      nodes,
      marks
    });
  }
  getEditors() {
    const editors = {};
    for (const config of this.configs.value) {
      if (!config.editor)
        continue;
      if ("node" in config) {
        editors[config.node.name] = config.editor;
      }
      if ("mark" in config) {
        editors[config.mark.name] = config.editor;
      }
    }
    return editors;
  }
  getMenuItems() {
    const menuItems = [];
    for (const config of this.configs.value) {
      if (!config.menuItems)
        continue;
      menuItems.push(...config.menuItems);
    }
    const editors = this.getEditors();
    if (Object.values(editors).length > 0) {
      menuItems.push(
        EditSelectedNodeMenuItem({
          editors,
          globalSettingsClassName: this.globalSettingsClassName.value,
          sizeBreakpoints: JSON.stringify(this.sizeBreakpoints.value)
        })
      );
    }
    if (this.globalSettingsClassName.value) {
      menuItems.push(EditGlobalSettingsMenuItem(this.globalSettingsClassName.value));
    }
    return menuItems;
  }
  willUpdate(_changedProperties) {
    super.willUpdate(_changedProperties);
    this._menuItems = this.getMenuItems();
  }
  connectedCallback() {
    super.connectedCallback();
    combineLatest([this.configs.observable]).pipe(debounceTime(300)).subscribe(([configs]) => {
      this.updateEditor();
    });
    this.addEventListener("blur", () => {
      this.commitValue("picked");
    });
  }
  update(changedProperties) {
    super.update(changedProperties);
    if (changedProperties.has("value") && this.view && this._schema) {
      const html2 = this.value.value || "";
      const node = document.createElement("div");
      node.innerHTML = html2;
      if (getHtmlString(this.view.state.doc, this.getStylings()) === html2) {
        return;
      }
      const newDoc = DOMParser.fromSchema(this._schema).parse(node);
      this.view.updateState(
        EditorState.create({
          doc: newDoc,
          plugins: this.view.state.plugins,
          schema: this.view.state.schema
        })
      );
    }
  }
  firstUpdated(_changedProperties) {
    super.firstUpdated(_changedProperties);
    this._schema = this.generateSchema();
    const editorElement = this.renderRoot.querySelector(`#${EDITOR_ID}`);
    if (!editorElement)
      throw new Error("editor or content not found");
    const html2 = this.value.value || "";
    const node = document.createElement("div");
    node.innerHTML = html2;
    const state2 = EditorState.create({
      schema: this._schema,
      plugins: this.getPlugins(this._schema),
      doc: DOMParser.fromSchema(this._schema).parse(node)
    });
    this.view = new EditorView(editorElement, {
      state: state2
    });
    this.sizeBreakpoints.observable.subscribe((breakpoints) => {
      if (!this.view)
        return;
      setDocAttribute({
        view: this.view,
        key: "size-breakpoints",
        value: breakpoints ? JSON.stringify(breakpoints) : ""
      });
    });
    this.globalSettingsClassName.observable.subscribe((globalSettingsClassName) => {
      if (!this.view)
        return;
      setDocAttribute({
        view: this.view,
        key: "global-settings-class-name",
        value: globalSettingsClassName ? JSON.stringify(globalSettingsClassName) : ""
      });
    });
  }
  commitValue(type) {
    const view = this.view;
    if (!view)
      return;
    const { state: state2 } = view;
    const value = getHtmlString(state2.doc, this.getStylings());
    this.value.next(value);
    this.dispatchEvent(
      new CustomEvent(`value-${type}`, {
        detail: { value }
      })
    );
  }
  updateEditor() {
    const editor = this.view;
    if (!editor)
      return;
    const schema = editor.state.schema;
    const newState = editor.state.reconfigure({
      plugins: this.getPlugins(schema)
    });
    editor.updateState(newState);
    this.requestUpdate();
  }
  render() {
    const maxWidth = this.sizeBreakpoints.value && this._selectedSize ? `${this.sizeBreakpoints.value[this._selectedSize]}px` : "100%";
    return html`
      ${this._getEditorStylings()}
      ${this.sizeBreakpoints.value ? html`
            <x-buildify-size-picker
              ${css({ marginBottom: "16px", display: "block" })}
              .value=${this._selectedSize}
              @value-picked=${(e) => {
      this._selectedSize = e.detail.value;
    }}
            ></x-buildify-size-picker>
          ` : nothing}
      <x-editor-toolbar .view=${this.view} .menuItems=${this._menuItems}></x-editor-toolbar>
      <node-doc
        mode="edit"
        id=${EDITOR_ID}
        ${css({
      width: maxWidth,
      maxWidth,
      margin: "0 auto",
      boxSizing: "border-box"
    })}
        spellcheck="true"
      >
      </node-doc>
    `;
  }
  //
  //
  //                                                        styles
  //
  //______________________________________________________________________________________________________________________________
  _getEditorStylings() {
    const styleElement = document.createElement("style");
    styleElement.textContent = unsafeCSS(`
      /* others */
      ::selection {
        color: inherit;
        background-color: inherit;
      }

      .ProseMirror.ProseMirror-focused {
        outline: none;
      }

      .ProseMirror {
        outline: none;
        padding: 0px 8px 16px 8px;
        /* prosemirror warns us if we don't set this */
        /* // TODO do we want to load prosemirror's CSS? Has Michi avoided that for a reason? */
        white-space: pre-wrap;
      }

      .ProseMirror-selectednode {
        position: relative;
        outline: 2px solid ${this.colors.accent};
      }

      /* custom stylings from configs */
      ${this.getStylings()}
    `).cssText;
    return styleElement;
  }
  styles() {
    return [
      ...super.styles(),
      {
        ":host": {
          display: "block",
          minWidth: "0"
        },
        "[wrapper]": {
          margin: this.spacing.md,
          "#editor": {
            margin: "4px",
            ".ProseMirror:focus-visible": {
              outline: "none"
            }
          }
        }
      }
    ];
  }
};
__decorateClass([
  property({ attribute: false })
], XEditor.prototype, "configs", 2);
__decorateClass([
  property({ attribute: false })
], XEditor.prototype, "globalSettingsClassName", 2);
__decorateClass([
  property({ attribute: false })
], XEditor.prototype, "sizeBreakpoints", 2);
__decorateClass([
  state()
], XEditor.prototype, "_selectedSize", 2);
XEditor = __decorateClass([
  customElement("x-editor")
], XEditor);
export {
  XEditor
};
